<script setup>
import { computed, ref } from 'vue'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import hljs from 'highlight.js/lib/common'
import 'highlight.js/styles/github.css'
import mdKatex from '@traptitech/markdown-it-katex'
import 'katex/dist/katex.min.css'
import { getStorageFileUrl } from '@/config/api'

const props = defineProps({
  message: { type: Object, required: true },
})

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// 代码块 wrapper：顶部语言标签 + 右上角复制按钮（复制交互通过事件委托实现，见 onContentClick）
const COPY_ICON_SVG =
  '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
  '<rect x="9" y="9" width="11" height="11" rx="2"></rect>' +
  '<path d="M5 15V6a2 2 0 0 1 2-2h9"></path></svg>'

function wrapCodeBlock(lang, highlightedHtml) {
  const display = lang || 'text'
  return (
    '<div class="code-block">' +
      '<div class="code-block__bar">' +
        `<span class="code-block__lang">${display}</span>` +
        '<button type="button" class="code-block__copy" aria-label="复制代码">' +
          COPY_ICON_SVG +
          '<span class="code-block__copy-label">复制</span>' +
        '</button>' +
      '</div>' +
      `<pre class="hljs"><code class="hljs${lang ? ' language-' + lang : ''}">${highlightedHtml}</code></pre>` +
    '</div>'
  )
}

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  typographer: false,
  highlight(code, lang) {
    const language = (lang || '').trim().toLowerCase()
    try {
      if (language && hljs.getLanguage(language)) {
        return wrapCodeBlock(language, hljs.highlight(code, { language, ignoreIllegals: true }).value)
      }
      const auto = hljs.highlightAuto(code)
      return wrapCodeBlock(auto.language || '', auto.value)
    } catch {
      return wrapCodeBlock(language, escapeHtml(code))
    }
  },
})

// 数学公式渲染：$...$ 行内，$$...$$ 块级，\(...\)  \[...\] 亦支持。
// output: 'html' 只产生 span 层级 DOM，避免 DOMPurify 吞掉 MathML/SVG 标签。
md.use(mdKatex, {
  throwOnError: false,
  errorColor: '#cc0000',
  output: 'html',
})

// 关闭 CommonMark 的"4 空格缩进代码块"规则：LLM 输出的表格/列表常被多余缩进误吞成代码块；
// 围栏代码块（```）走的是独立的 fence 规则，不受影响。
md.disable('code')

// 让所有外链在新标签页打开、带 noopener，避免对话被劫持跳走
const defaultLinkRender = md.renderer.rules.link_open
  || function (tokens, idx, options, env, self) { return self.renderToken(tokens, idx, options) }
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const token = tokens[idx]
  const hrefIdx = token.attrIndex('href')
  const href = hrefIdx >= 0 ? token.attrs[hrefIdx][1] : ''
  if (href && /^https?:/i.test(href)) {
    token.attrSet('target', '_blank')
    token.attrSet('rel', 'noopener noreferrer')
  }
  return defaultLinkRender(tokens, idx, options, env, self)
}

const rendered = computed(() => {
  const raw = String(props.message?.content ?? '')
  if (!raw) return ''
  const html = md.render(raw)
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel'],
  })
})

const isAssistant = computed(() => props.message?.role === 'assistant')
const hasContent = computed(() => !!String(props.message?.content ?? '').trim())

const userMediaPreview = computed(() => {
  if (props.message?.role !== 'user') return null
  const mp = props.message.mediaPreview
  if (mp?.url && (mp.kind === 'image' || mp.kind === 'video')) {
    return { kind: mp.kind, url: mp.url }
  }
  const a = props.message.attachments?.[0]
  if (a?.path && (a.type === 'image' || a.type === 'video')) {
    const url = getStorageFileUrl(a.path)
    if (url) return { kind: a.type === 'video' ? 'video' : 'image', url }
  }
  return null
})

/** 流式中 assistantMsg 会显式设 completed:false，此时不显示工具条；结束后为 true，历史/旧数据无字段时视为已完成 */
const showToolbar = computed(() => {
  if (!isAssistant.value || !hasContent.value) return false
  return props.message.completed !== false
})

const copied = ref(false)
const feedback = ref(null) // 'like' | 'dislike' | null

async function onCopy() {
  const text = String(props.message?.content ?? '')
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // 非安全上下文 / 老浏览器降级方案
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
    } catch (error) {
      // ignore fallback copy errors
      void error
    }
    document.body.removeChild(ta)
  }
  copied.value = true
  setTimeout(() => { copied.value = false }, 1500)
}

function onFeedback(v) {
  feedback.value = feedback.value === v ? null : v
}

// 代码块的"复制"按钮交互：事件委托在 .markdown 容器上
function onContentClick(e) {
  const btn = e.target.closest?.('.code-block__copy')
  if (!btn) return
  const block = btn.closest('.code-block')
  const text = block?.querySelector('code')?.innerText ?? ''
  const label = btn.querySelector('.code-block__copy-label')

  const done = () => {
    btn.classList.add('is-ok')
    if (label) label.textContent = '已复制'
    setTimeout(() => {
      btn.classList.remove('is-ok')
      if (label) label.textContent = '复制'
    }, 1500)
  }
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => done())
  } else {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
    } catch (error) {
      // ignore fallback copy errors
      void error
    }
    document.body.removeChild(ta)
    done()
  }
}
</script>

<template>
  <div class="msg" :class="`msg--${message.role}`">
    <template v-if="isAssistant">
      <div class="bubble">
        <div class="content markdown" v-html="rendered" @click="onContentClick"></div>
      </div>
    </template>
    <template v-else>
      <!-- 图/视频在气泡外（上），文案单独在气泡内，与主聊天一致 -->
      <div v-if="userMediaPreview" class="user-media-outside">
        <img
          v-if="userMediaPreview.kind === 'image'"
          :src="userMediaPreview.url"
          alt="上传的图片"
          class="user-media-outside__media"
        />
        <video
          v-else
          :src="userMediaPreview.url"
          class="user-media-outside__media user-media-outside__media--video"
          controls
          playsinline
          preload="metadata"
        />
      </div>
      <div v-if="message.content" class="bubble">
        <div class="user-msg__text">{{ message.content }}</div>
      </div>
    </template>

    <div v-if="showToolbar" class="toolbar">
      <button
        class="toolbar__btn"
        :class="{ 'is-ok': copied }"
        :title="copied ? '已复制' : '复制全部内容'"
        @click="onCopy"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="9" y="9" width="11" height="11" rx="2"/>
          <path d="M5 15V6a2 2 0 0 1 2-2h9"/>
        </svg>
        <span>{{ copied ? '已复制' : '复制' }}</span>
      </button>

      <span class="toolbar__sep" aria-hidden="true"></span>
      <span class="toolbar__hint">您是否满意回答的内容?</span>

      <button
        class="toolbar__icon"
        :class="{ 'is-like': feedback === 'like' }"
        title="满意"
        @click="onFeedback('like')"
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M7 21V10"/>
          <path d="M2 10h5v11H3a1 1 0 0 1-1-1V10z"/>
          <path d="M7 10l4.2-6.5c.5-.8 1.6-1 2.3-.4.5.4.7.9.6 1.5l-.8 3.9h5.4a2 2 0 0 1 2 2.3l-1.4 7.9A2 2 0 0 1 17.4 21H7"/>
        </svg>
      </button>

      <button
        class="toolbar__icon"
        :class="{ 'is-dislike': feedback === 'dislike' }"
        title="不满意"
        @click="onFeedback('dislike')"
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M17 3v11"/>
          <path d="M22 14h-5V3h4a1 1 0 0 1 1 1v10z"/>
          <path d="M17 14l-4.2 6.5c-.5.8-1.6 1-2.3.4-.5-.4-.7-.9-.6-1.5l.8-3.9H5.3a2 2 0 0 1-2-2.3l1.4-7.9A2 2 0 0 1 6.6 3H17"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.msg { display: flex; flex-direction: column; }
.msg--user {
  align-items: flex-end;
  gap: 8px;
}
.msg--assistant { align-items: flex-start; }

.bubble {
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 10px;
  background: #f3f4f6;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}
.msg--user .bubble {
  background: #eef0fa;
  color: #1f2430;
  border-radius: 14px;
  white-space: normal;
}
.user-msg__text {
  white-space: pre-wrap;
  word-break: break-word;
}
/* 用户媒体：无气泡底色，仅圆角预览，叠在文字气泡之上 */
.user-media-outside {
  max-width: min(240px, 70vw);
  padding: 0;
  background: transparent;
  border-radius: 10px;
  overflow: hidden;
  line-height: 0;
}
.user-media-outside__media {
  display: block;
  width: 100%;
  height: auto;
  max-height: min(34vh, 220px);
  object-fit: contain;
  border-radius: 10px;
  vertical-align: middle;
}
.user-media-outside__media--video {
  max-height: min(26vh, 168px);
  background: #000;
}
/* 助手消息：无气泡底色，直接嵌在对话区（跟输入框同底色），占满宽度更好显示表格/代码 */
.msg--assistant .bubble {
  max-width: 100%;
  width: 100%;
  padding: 4px 2px;
  background: transparent;
  border-radius: 0;
  white-space: normal;
}

/* ---- Markdown 排版 ---- */
.markdown :deep(h1),
.markdown :deep(h2),
.markdown :deep(h3),
.markdown :deep(h4) {
  margin: 14px 0 8px;
  line-height: 1.35;
  font-weight: 600;
  color: #1f2430;
}
.markdown :deep(h1) { font-size: 18px; }
.markdown :deep(h2) { font-size: 16px; }
.markdown :deep(h3) { font-size: 15px; }
.markdown :deep(h4) { font-size: 14px; }

.markdown :deep(p) { margin: 6px 0; }

.markdown :deep(strong) { font-weight: 600; color: #111827; }
.markdown :deep(em) { font-style: italic; }

.markdown :deep(ul),
.markdown :deep(ol) {
  margin: 6px 0;
  padding-left: 22px;
}
.markdown :deep(li) { margin: 2px 0; }
.markdown :deep(li > p) { margin: 0; }

.markdown :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 12px 0;
}

.markdown :deep(blockquote) {
  margin: 8px 0;
  padding: 4px 10px;
  color: #4b5563;
  border-left: 3px solid #c9d1ff;
  background: #f8faff;
  border-radius: 4px;
}

/* 行内 code（不含 .hljs，排除代码块里的 <code>） */
.markdown :deep(:not(pre) > code) {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.92em;
  padding: 1px 5px;
  background: #eef0f4;
  border-radius: 4px;
  color: #b83280;
}

/* ---- 代码块：浅灰底、无边框、顶部带语言标签+复制按钮 ---- */
.markdown :deep(.code-block) {
  margin: 8px 0;
  border-radius: 10px;
  background: #f6f7f9;
  overflow: hidden;
}
.markdown :deep(.code-block__bar) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px 2px;
  font-size: 12px;
  color: #8891a3;
  user-select: none;
}
.markdown :deep(.code-block__lang) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  text-transform: lowercase;
}
.markdown :deep(.code-block__copy) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  border-radius: 6px;
  font: inherit;
  transition: background 0.15s, color 0.15s;
}
.markdown :deep(.code-block__copy:hover) {
  background: rgba(0, 0, 0, 0.05);
  color: #1f2430;
}
.markdown :deep(.code-block__copy.is-ok) { color: #0a9a5f; }

/* 包进 .code-block 后，内部 pre 不需要独立背景/圆角 */
.markdown :deep(.code-block > pre),
.markdown :deep(.code-block > pre.hljs),
.markdown :deep(.code-block > pre code.hljs) {
  margin: 0;
  padding: 6px 14px 12px;
  border: none;
  border-radius: 0;
  background: transparent;
  font-size: 12.5px;
  line-height: 1.55;
  overflow-x: auto;
}
.markdown :deep(pre code) {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
}

.markdown :deep(table) {
  border-collapse: collapse;
  margin: 8px 0;
  font-size: 13px;
  width: 100%;
}
.markdown :deep(th),
.markdown :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 6px 10px;
  text-align: left;
}
.markdown :deep(thead th) {
  background: #f3f4f6;
  font-weight: 600;
}
.markdown :deep(tbody tr:nth-child(even)) {
  background: #fafafa;
}

.markdown :deep(a) {
  color: #1b7cff;
  text-decoration: none;
}
.markdown :deep(a:hover) { text-decoration: underline; }

/* 首尾元素去边距，避免气泡出现多余空行 */
.markdown :deep(> :first-child) { margin-top: 0; }
.markdown :deep(> :last-child)  { margin-bottom: 0; }

/* KaTeX 公式：块级居中 + 可横向滚动，行内自适应 */
.markdown :deep(.katex-display) {
  margin: 10px 0;
  padding: 4px 0;
  overflow-x: auto;
  overflow-y: hidden;
}
.markdown :deep(.katex) {
  font-size: 1.02em;
}

/* ---- 消息工具条（仅 assistant） ---- */
.toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 2px 2px;
  color: #8b94a6;
  font-size: 12px;
  user-select: none;
}
.toolbar__btn,
.toolbar__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}
.toolbar__icon { width: 26px; padding: 4px; }
.toolbar__btn:hover,
.toolbar__icon:hover {
  background: #f0f2f6;
  color: #1f2430;
}
.toolbar__btn.is-ok { color: #0a9a5f; }
.toolbar__icon.is-like { color: #1b7cff; background: #e8f2ff; }
.toolbar__icon.is-dislike { color: #e55353; background: #fdeaea; }

.toolbar__sep {
  width: 1px;
  height: 12px;
  background: #dde1e8;
  margin: 0 4px;
}
.toolbar__hint { color: #8b94a6; margin-right: 2px; }
</style>
